















































































































































import { Component, Vue, Prop, PropSync, Watch, Ref, InjectReactive } from 'vue-property-decorator';
import { GQL_PaycheckPeriodSave } from '@/queries/global';
import { generateUID, WithUID } from '@/utils/utils';
import { IRules, sValidation } from '@/components/UI/Form/ValidationModule';
import { validateRequired, validateType } from '@/components/UI/Form/Validations';
import range from 'lodash/range';
import SForm from '@/components/UI/Form/SForm.vue';
import { SmartQuery } from 'vue-apollo-decorator';
import { ALL_PAYCHECK_PERIODS } from '@/queries/PaycheckPeriod';
import { GQL_savePaycheckPeriodVariables } from '@/queries/__generated__/savePaycheckPeriod';
import { CURRENT_UNIT } from '@/queries/Application';
import { GQL_currentUnit, GQL_currentUnit_currentUnit } from '@/queries/__generated__/currentUnit';
import i18n from '@/i18n';
import { GQL_currentUnitPaycheckPeriodsInUse } from '@/queries/__generated__/currentUnitPaycheckPeriodsInUse';
import PermissionService from '@/utils/permission-service';
import { sort } from '../UI/Table/sorting';
import gql from 'graphql-tag';

const ALL_PAYCHECK_PERIODS_IN_USE = gql`
  query currentUnitPaycheckPeriodsInUse {
    currentUnit {
      id
      paycheckPeriods {
        id
        isInUse
      }
    }
  }
`;

const SAVE_PAYCHECK_PERIODS = gql`
  mutation savePaycheckPeriod($unitId: ID!, $paycheckPeriods: [PaycheckPeriodSave!]!) {
    savePaycheckPeriod(input: { unitId: $unitId, paycheckPeriods: $paycheckPeriods }) {
      paycheckPeriods {
        id
      }
    }
  }
`;



@Component
export default class PaycheckPeriodsTableForm extends Vue {
  @SmartQuery<PaycheckPeriodsTableForm, GQL_currentUnit>({
    query: CURRENT_UNIT,
  }) private currentUnit?: GQL_currentUnit_currentUnit;

  @SmartQuery<PaycheckPeriodsTableForm, GQL_currentUnitPaycheckPeriodsInUse>({
    query: ALL_PAYCHECK_PERIODS_IN_USE,
    fetchPolicy: 'no-cache',
    update: data => data.currentUnit.paycheckPeriods
      .filter(p => p.isInUse)
      .reduce((obj, p) => { obj[p.id] = true; return obj }, {} as { [id: string]: true }),
  }) private paycheckPeriodsInUse?: { [id: string]: true } = {};

  @Prop({ type: Array, required: true }) private paycheckPeriods!: WithUID<GQL_PaycheckPeriodSave>[];
  @Prop({ type: Number, required: true }) private year!: number;
  @PropSync('valid', { type: Boolean, default: true }) private validFormSync!: boolean;
  @PropSync('dirty', { type: Boolean, default: false }) private dirtyFormSync!: boolean;

  @InjectReactive() private editable!: boolean;

  @Ref() private formRef?: SForm;

  private strictlyMonthlyPeriods = true;

  private PermissionService = PermissionService;

  // example: ['2019-01', ..., '2019-12']
  private get bookkeepingPeriods(): string[] {
    return range(1, 13).map(month => `${this.year}-${String(month).padStart(2, '0')}`);
  }

  private get notDeletedPaycheckPeriods(): WithUID<GQL_PaycheckPeriodSave>[] {
    return this.paycheckPeriods.filter(p => !p._destroy);
  }

  @Watch('paycheckPeriods', { immediate: true })
  private ensureUIDsAreSet(): void {
    this.paycheckPeriods.forEach(p => {
      if (!p.$_UID) p.$_UID = generateUID();
    });
  }

  private mounted() {
    this.manuallySort(this.sortColumn, this.sortDirecton);
  }

  private addPaycheckPeriod(): void {
    this.paycheckPeriods.push({
      id: null,
      name: null,
      bookkeepingPeriod: null,
      startDate: null,
      endDate: null,
      journalDate: null,
      $_UID: generateUID(),
    });
  }

  private removePaycheckPeriod(paycheckPeriod: WithUID<GQL_PaycheckPeriodSave>): void {
    if (paycheckPeriod.id) {
      Vue.set(paycheckPeriod, '_destroy', true);
    } else {
      this.paycheckPeriods.splice(
        this.paycheckPeriods.indexOf(paycheckPeriod),
        1,
      );
    }
  }

  public resetFormDirty(): void {
    this.$nextTick(() => this.formRef && this.formRef.resetDirty());
  }

  public setFormDirty(): void {
    this.$nextTick(() => this.formRef && this.formRef.setDirty());
  }

  // validations

  private strictMonthlyPeriodValidation: sValidation = values => {
    if (
      !values.dataValue ||
      !values.formElements.bookkeepingPeriod ||
      !values.formElements.bookkeepingPeriod[values.uid as string] ||
      !values.formElements.bookkeepingPeriod[values.uid as string].dataValue
    ) return true;

    return (
      this.$dayjs.strict(values.dataValue).format('YYYY-MM') ===
      values.formElements.bookkeepingPeriod[values.uid as string].dataValue
    );
  }

  private periodOverlapValidation: sValidation = values => {
    if (!values.dataValue) return true;

    const date = this.$dayjs.strict(values.dataValue);

    return this.notDeletedPaycheckPeriods.every(p =>
      p.$_UID === values.uid || // return true if it's the same period
      !p.startDate || !p.endDate || // return true if period doesn't have dates
      !date.isBetween(p.startDate, p.endDate, 'day', '[]'), // return true if date doesn't overlap
    );
  }

  private rules = {
    name: [ { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('PaycheckPeriod', 'name') }) } ],
    bookkeepingPeriod: [ { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('PaycheckPeriod', 'bookkeepingPeriod') }) } ],
    startDate: [
      { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('PaycheckPeriod', 'startDate') }) },
      { validation: validateType('date'), message: i18n => i18n.t('GQL.Type.date') },
      { validation: this.strictMonthlyPeriodValidation, triggerBy: [ 'bookkeepingPeriod' ], if: () => this.strictlyMonthlyPeriods, message: i18n => i18n.t('PaycheckPeriod.dateMustBeInPeriod') },
      { validation: this.periodOverlapValidation, triggerBy: [ 'startDate', 'endDate' ], message: i18n => i18n.t('PaycheckPeriod.dateCannotOverlap') },
    ],
    endDate: [
      { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('PaycheckPeriod', 'endDate') }) },
      { validation: validateType('date'), message: i18n => i18n.t('GQL.Type.date') },
      { validation: this.strictMonthlyPeriodValidation, triggerBy: [ 'bookkeepingPeriod' ], if: () => this.strictlyMonthlyPeriods, message: i18n => i18n.t('PaycheckPeriod.dateMustBeInPeriod') },
      { validation: this.periodOverlapValidation, triggerBy: [ 'startDate', 'endDate' ], message: i18n => i18n.t('PaycheckPeriod.dateCannotOverlap') },
    ],
    journalDate: [
      { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('PaycheckPeriod', 'journalDate') }) },
      { validation: validateType('date'), message: i18n => i18n.t('GQL.Type.date') },
      { validation: this.strictMonthlyPeriodValidation, triggerBy: [ 'bookkeepingPeriod' ], if: () => this.strictlyMonthlyPeriods, message: i18n => i18n.t('PaycheckPeriod.dateMustBeInPeriod') },
    ],
  } as IRules;

  // saving

  private SAVE_PAYCHECK_PERIODS = SAVE_PAYCHECK_PERIODS;
  private ALL_PAYCHECK_PERIODS = ALL_PAYCHECK_PERIODS;
  private saveInProgress = false;

  private formatDataForMutation(): GQL_savePaycheckPeriodVariables {
    return {
      unitId: this.currentUnit!.id,
      paycheckPeriods: this.paycheckPeriods.map(p => ({
        ...p,
        $_UID: undefined,
      })),
    };
  }

  private sortColumn = 'startDate';
  private sortDirecton = 'asc';

  private manuallySort(column: string, direction: string) {
    this.sortColumn = column;
    this.sortDirecton = direction;

    this.paycheckPeriods = sort(this.paycheckPeriods, column, direction);
  }

  public savePaycheckPeriods(): Promise<boolean> {
    this.saveInProgress = true;

    return this.$apollo.mutate({
      mutation: SAVE_PAYCHECK_PERIODS,
      variables: this.formatDataForMutation(),
      refetchQueries: [ { query: ALL_PAYCHECK_PERIODS } ],
    }).then(() => {
      this.manuallySort(this.sortColumn, this.sortDirecton);
      // i18n.t used instead of this.$t because function is async and component calling it might be destroyed, making 'this' undefined
      this.$sMessage.success(i18n.t('Message.saveSuccess'));
      this.resetFormDirty();
      return true;
    }).catch(() => {
      return false;
    }).finally(() => {
      this.saveInProgress = false;
    });
  }
}
